// File: frontend/src/components/common/Dropdown.js

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import styles from './Dropdown.module.css';

const Dropdown = ({
  value = '',
  onChange,
  options = [],
  label,
  placeholder = 'Select an option',
  required = false,
  error = null,
  disabled = false,
  name,
  isOpen = false, // ✅ NEW: Controlled open state
  onOpenChange // ✅ NEW: Callback when open state changes
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (isOpen && onOpenChange) {
          onOpenChange(false);
        }
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onOpenChange]);

  const handleOptionSelect = (optionValue) => {
    if (onOpenChange) {
      onOpenChange(false);
    }
    setIsFocused(false);
    
    if (onChange) {
      onChange(optionValue);
    }
  };

  const handleToggle = () => {
    if (!disabled && onOpenChange) {
      onOpenChange(!isOpen);
      setIsFocused(!isOpen);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (onOpenChange) {
        onOpenChange(false);
      }
      setIsFocused(false);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  // Find selected option label
  const selectedOption = options.find(opt => opt.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  return (
    <div className={styles.dropdownContainer}>
      {label && (
        <label className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div 
        className={`${styles.dropdownWrapper} ${error ? styles.hasError : ''} ${isFocused ? styles.focused : ''}`}
        ref={dropdownRef}
      >
        <button
          ref={buttonRef}
          type="button"
          className={styles.dropdownButton}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          name={name}
        >
          <span className={`${styles.dropdownText} ${!value ? styles.placeholder : ''}`}>
            {displayText}
          </span>
          <ChevronDown 
            size={16} 
            className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
          />
        </button>

        {isOpen && (
          <div className={styles.dropdownMenu} role="listbox">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`${styles.dropdownOption} ${value === option.value ? styles.dropdownOptionActive : ''}`}
                onClick={() => handleOptionSelect(option.value)}
                role="option"
                aria-selected={value === option.value}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}
    </div>
  );
};

export default Dropdown;